import { useParams, Link } from 'react-router-dom';
import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { comics, categories } from '@/mocks/comics';

export default function ComicDetail() {
  const { id } = useParams<{ id: string }>();
  const [showAllChapters, setShowAllChapters] = useState(false);

  const comic = comics.find(c => c.id === Number(id));

  if (!comic) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="pt-20 min-h-screen flex items-center justify-center bg-background-50">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-error-warning-line text-3xl text-foreground-600"></i>
            </div>
            <h2 className="text-lg font-bold text-foreground-950 mb-2">漫画未找到</h2>
            <p className="text-sm text-foreground-600 mb-4">抱歉，您查找的漫画不存在或已被移除。</p>
            <Link
              to="/"
              className="inline-block px-5 py-2 bg-accent-500 text-background-50 rounded-full text-sm cursor-pointer whitespace-nowrap"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const categoryName = categories.find(c => c.slug === comic.category)?.name || '';
  const displayChapters = showAllChapters ? comic.chapters : comic.chapters.slice(0, 5);

  const relatedComics = comics
    .filter(c => c.id !== comic.id && c.category === comic.category)
    .slice(0, 4);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${comic.title} - 韩漫屋韩国免费漫画`,
    description: comic.description,
    author: {
      '@type': 'Person',
      name: comic.author,
    },
    datePublished: comic.chapters[0]?.updateDate || '2026-01-01',
    dateModified: comic.chapters[comic.chapters.length - 1]?.updateDate || '2026-08-04',
    publisher: {
      '@type': 'Organization',
      name: '韩漫屋',
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: window.location.origin + (__BASE_PATH__ || ''),
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: categoryName,
        item: window.location.origin + (__BASE_PATH__ || '') + '/category/' + comic.category,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: comic.title,
        item: window.location.origin + (__BASE_PATH__ || '') + '/comic/' + comic.id,
      },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <Navbar />

      <main className="pt-16">
        {/* Breadcrumb */}
        <div className="w-full px-4 md:px-6 py-3 bg-background-50 border-b border-background-200/70">
          <nav className="max-w-7xl mx-auto flex items-center gap-1.5 text-xs text-foreground-600" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-accent-500 transition-colors cursor-pointer">首页</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <Link to={`/category/${comic.category}`} className="hover:text-accent-500 transition-colors cursor-pointer">{categoryName}</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-foreground-950">{comic.title}</span>
          </nav>
        </div>

        {/* Comic Detail Header */}
        <section className="w-full px-4 md:px-6 py-8 md:py-10 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col md:flex-row gap-6 md:gap-8">
              {/* Cover */}
              <div className="w-36 md:w-48 flex-shrink-0 mx-auto md:mx-0">
                <div className="aspect-[3/4] rounded-lg overflow-hidden bg-background-100">
                  <img
                    src={comic.cover}
                    alt={comic.title}
                    title={`${comic.title} - 韩漫屋韩国免费漫画在线阅读`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              {/* Info */}
              <div className="flex-1 text-center md:text-left">
                <h1 className="text-xl md:text-2xl font-bold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
                  {comic.title}
                </h1>

                <div className="flex flex-wrap items-center justify-center md:justify-start gap-2 mt-3">
                  <Link
                    to={`/category/${comic.category}`}
                    className="px-3 py-1 bg-accent-100 text-accent-700 rounded-full text-xs cursor-pointer whitespace-nowrap hover:bg-accent-200/70 transition-colors"
                  >
                    {categoryName}
                  </Link>
                  {comic.tags.map((tag) => (
                    <span key={tag} className="px-3 py-1 bg-background-100 text-foreground-600 rounded-full text-xs whitespace-nowrap">
                      {tag}
                    </span>
                  ))}
                  <span className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
                    comic.status === '连载中'
                      ? 'bg-accent-500 text-background-50'
                      : 'bg-foreground-600 text-background-50'
                  }`}>
                    {comic.status}
                  </span>
                </div>

                <div className="flex flex-wrap items-center justify-center md:justify-start gap-4 mt-4 text-xs text-foreground-600">
                  <span>作者：<strong className="text-foreground-950">{comic.author}</strong></span>
                  <span className="flex items-center gap-1">
                    <i className="ri-star-fill text-accent-500"></i>
                    <strong className="text-foreground-950">{comic.rating}</strong> 分
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-eye-line"></i>
                    <strong className="text-foreground-950">{(comic.views / 10000).toFixed(0)}万</strong> 次阅读
                  </span>
                  <span>共 <strong className="text-foreground-950">{comic.chapters.length}</strong> 话</span>
                </div>

                <p className="mt-4 text-sm text-foreground-700 leading-relaxed max-w-xl">
                  {comic.description}
                </p>

                <div className="flex flex-col sm:flex-row items-center justify-center md:justify-start gap-3 mt-5">
                  <button className="px-6 py-2.5 bg-accent-500 text-background-50 rounded-full text-sm font-medium cursor-pointer whitespace-nowrap hover:opacity-90 transition-opacity">
                    开始阅读第一话
                  </button>
                  <button className="px-6 py-2.5 bg-background-100 text-foreground-700 rounded-full text-sm cursor-pointer whitespace-nowrap hover:bg-background-200/70 transition-colors">
                    <i className="ri-bookmark-line mr-1.5"></i>收藏
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Chapter List */}
        <section className="w-full px-4 md:px-6 py-8 md:py-10 bg-background-100/70">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-lg font-bold text-foreground-950 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
              章节列表
              <span className="text-sm font-normal text-foreground-600 ml-2">
                共 {comic.chapters.length} 话
              </span>
            </h2>

            <div className="space-y-2">
              {displayChapters.map((chapter, index) => (
                <div
                  key={chapter.id}
                  className="flex items-center justify-between p-3 md:p-4 rounded-lg bg-background-50 hover:bg-background-100 transition-colors cursor-pointer"
                >
                  <div className="flex items-center gap-3">
                    <span className="w-6 h-6 flex items-center justify-center rounded-full bg-accent-100 text-accent-700 text-xs font-medium flex-shrink-0">
                      {chapter.id}
                    </span>
                    <span className="text-sm font-medium text-foreground-950">
                      {chapter.title}
                    </span>
                    {index === 0 && (
                      <span className="px-2 py-0.5 bg-accent-500 text-background-50 rounded text-xs font-medium whitespace-nowrap">
                        NEW
                      </span>
                    )}
                  </div>
                  <span className="text-xs text-foreground-600 flex-shrink-0 ml-3">{chapter.updateDate}</span>
                </div>
              ))}
            </div>

            {comic.chapters.length > 5 && (
              <button
                onClick={() => setShowAllChapters(!showAllChapters)}
                className="mt-4 w-full py-2.5 text-sm text-accent-500 hover:text-accent-600 cursor-pointer transition-colors rounded-lg bg-background-50"
              >
                {showAllChapters ? '收起' : `查看全部 ${comic.chapters.length} 话`}
                {showAllChapters ? <i className="ri-arrow-up-s-line ml-1"></i> : <i className="ri-arrow-down-s-line ml-1"></i>}
              </button>
            )}
          </div>
        </section>

        {/* Related Comics */}
        {relatedComics.length > 0 && (
          <section className="w-full px-4 md:px-6 py-8 md:py-10 bg-background-50">
            <div className="max-w-7xl mx-auto">
              <h2 className="text-lg font-bold text-foreground-950 mb-5" style={{ fontFamily: 'var(--font-heading)' }}>
                同类推荐
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
                {relatedComics.map((rc) => (
                  <Link
                    key={rc.id}
                    to={`/comic/${rc.id}`}
                    className="group cursor-pointer"
                  >
                    <div className="relative aspect-[3/4] rounded-lg overflow-hidden bg-background-100 mb-3">
                      <img
                        src={rc.cover}
                        alt={rc.title}
                        title={`${rc.title} - 韩漫屋${categoryName}漫画免费阅读`}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                    <h3 className="text-sm font-medium text-foreground-950 group-hover:text-accent-500 transition-colors line-clamp-1">
                      {rc.title}
                    </h3>
                    <p className="text-xs text-foreground-600 mt-0.5">{rc.author}</p>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />
    </>
  );
}